import type { TaskTrackerSummaryDeadlineFilter } from './taskTrackerBoardSummary.types'
import {
  isTaskTrackerStickerFilterRangeDraft,
  type TaskTrackerStickerFilterDraftValue,
} from './taskTrackerBoard.stickerFilters.utils'

export type TaskTrackerSummaryDeadlineFilterOperator =
  TaskTrackerSummaryDeadlineFilter['operator']

export type TaskTrackerSummaryDeadlineFilterDraft = {
  operator: TaskTrackerSummaryDeadlineFilterOperator
  from?: string | null
  to?: string | null
  days?: number | null
}

export const TASK_TRACKER_SUMMARY_DEADLINE_WITHIN_DAYS_DEFAULT = 7

export const TASK_TRACKER_SUMMARY_DEADLINE_FILTER_OPERATOR_OPTIONS: Array<{
  value: TaskTrackerSummaryDeadlineFilterOperator
  label: string
}> = [
  { value: 'today', label: 'Сегодня' },
  { value: 'within_days', label: 'В ближайшие N дней' },
  { value: 'set', label: 'Установлен' },
  { value: 'unset', label: 'Не установлен' },
  { value: 'expired', label: 'Истёк' },
  { value: 'not_expired', label: 'Не истёк' },
  { value: 'range', label: 'Диапазон дат' },
]

export type SummaryDeadlineFilterDraftInput =
  | TaskTrackerStickerFilterDraftValue
  | TaskTrackerSummaryDeadlineFilterDraft
  | null

export const isTaskTrackerSummaryDeadlineFilterDraft = (
  value: SummaryDeadlineFilterDraftInput | undefined,
): value is TaskTrackerSummaryDeadlineFilterDraft =>
  value != null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  'operator' in value &&
  typeof (value as TaskTrackerSummaryDeadlineFilterDraft).operator === 'string'

const normalizeWithinDaysValue = (value: unknown): number | null => {
  const days = Number(value)
  if (!Number.isFinite(days) || days <= 0) return null
  return Math.trunc(days)
}

export const normalizeSummaryDeadlineFilterDraft = (
  value: SummaryDeadlineFilterDraftInput | undefined,
): TaskTrackerSummaryDeadlineFilterDraft | null => {
  if (isTaskTrackerSummaryDeadlineFilterDraft(value)) {
    if (value.operator === 'within_days') {
      return {
        operator: 'within_days',
        days: normalizeWithinDaysValue(value.days),
      }
    }

    return value
  }

  if (isTaskTrackerStickerFilterRangeDraft(value)) {
    return {
      operator: 'range',
      from: value.from ?? null,
      to: value.to ?? null,
    }
  }

  return null
}

export const isTaskTrackerSummaryDeadlineFilterDraftEmpty = (
  value: SummaryDeadlineFilterDraftInput | undefined,
) => {
  const draft = normalizeSummaryDeadlineFilterDraft(value)
  if (!draft) return true

  if (draft.operator === 'range') {
    return !draft.from?.trim() && !draft.to?.trim()
  }

  if (draft.operator === 'within_days') {
    return normalizeWithinDaysValue(draft.days) == null
  }

  return false
}

export const createEmptySummaryDeadlineRangeDraft =
  (): TaskTrackerSummaryDeadlineFilterDraft => ({
    operator: 'range',
    from: null,
    to: null,
  })

export const createEmptySummaryDeadlineWithinDaysDraft =
  (): TaskTrackerSummaryDeadlineFilterDraft => ({
    operator: 'within_days',
    days: TASK_TRACKER_SUMMARY_DEADLINE_WITHIN_DAYS_DEFAULT,
  })

export const isTaskTrackerSummaryDeadlineWithinDaysIncomplete = (
  value: SummaryDeadlineFilterDraftInput | undefined,
) => {
  if (!isTaskTrackerSummaryDeadlineFilterDraft(value)) return false
  if (value.operator !== 'within_days') return false
  return normalizeWithinDaysValue(value.days) == null
}
